import {
  ReactElement, useCallback, useEffect, useRef,
} from 'react';
import { YearSelectionContainer } from './styles';
import { Button } from 'components';
import { ChevronIcon } from 'icons';
import { useFormContext } from 'react-hook-form';
import { FormValues } from '../Form/types';
import { defaultConsumptionFormValue } from '../Form/constants';
import { colors } from '~/styles/colors';

interface YearSelectionProps {
  currentYear: number;
  loading: boolean;
  onChangeYear: (year: number) => void;
}

const minYear = 2018;

export function YearSelection({
  currentYear,
  loading,
  onChangeYear,
}: Readonly<YearSelectionProps>): ReactElement {
  const { getValues, reset } = useFormContext<FormValues>();
  const firstReference = useRef<boolean>(false);

  function onDecreaseYear(): void {
    const newYear = currentYear - 1;
    if (newYear < minYear) return;

    handleNewYearFormValue(newYear.toString());
    onChangeYear(newYear);
  }

  function onIncreaseYear(): void {
    const newYear = currentYear + 1;

    handleNewYearFormValue(newYear.toString());
    onChangeYear(newYear);
  }

  const handleNewYearFormValue = useCallback((year: string): void => {
    const formValues = getValues(year);

    if (!formValues) {
      const currentValues = getValues();
      reset({
        ...currentValues,
        [year]: defaultConsumptionFormValue,
      });
    }
  }, [getValues, reset]);

  useEffect(() => {
    if (currentYear && !firstReference.current) {
      firstReference.current = true;
      handleNewYearFormValue(currentYear.toString());
    }
  }, [currentYear, handleNewYearFormValue]);

  return (
    <YearSelectionContainer>
      <Button
        variant="primary-filled"
        onClick={onDecreaseYear}
        disabled={loading || currentYear === minYear}
        type="button"
      >
        <ChevronIcon
          color={loading || currentYear === minYear ? colors.icon['disabled-300'] : colors.icon['white-0']}
        />
      </Button>
      <span>{currentYear}</span>
      <Button
        variant="primary-filled"
        onClick={onIncreaseYear}
        disabled={loading}
        type="button"
      >
        <ChevronIcon
          color={loading ? colors.icon['disabled-300'] : colors.icon['white-0']}
          rotate="180deg"
        />
      </Button>
    </YearSelectionContainer>
  );
}
